import { Injectable, inject } from '@angular/core';
import { Storage } from '@ionic/storage-angular';
import {
  Action,
  NgxsAfterBootstrap,
  Selector,
  State,
  StateContext,
} from '@ngxs/store';
import { TypeOfGood } from '@wilson/activities/symbols';
import { OrganizationalUnitService } from '@wilson/organizational-unit';
import { catchError, finalize, map, Observable, of, tap } from 'rxjs';
import { InitializeTransportGoodsState } from './transport-goods.action';

export interface TransportGoodsStateModel {
  transportGoods: TypeOfGood[];
  isLoading: boolean;
}

const STATE_KEY = 'transportGoods';

const defaults: TransportGoodsStateModel = {
  transportGoods: [],
  isLoading: false,
};

@State<TransportGoodsStateModel>({
  name: STATE_KEY,
  defaults,
})
@Injectable()
export class TransportGoodsState implements NgxsAfterBootstrap {
  private readonly orgUnitService = inject(OrganizationalUnitService);
  private readonly storage = inject(Storage);

  @Selector()
  static transportGoods(state: TransportGoodsStateModel): TypeOfGood[] {
    return state.transportGoods;
  }

  @Selector()
  static isLoading(state: TransportGoodsStateModel): boolean {
    return state.isLoading;
  }

  async ngxsAfterBootstrap(
    ctx: StateContext<TransportGoodsStateModel>,
  ): Promise<void> {
    const stored: TypeOfGood[] | null = await this.storage.get(STATE_KEY);
    if (stored?.length) {
      ctx.patchState({ transportGoods: stored });
    }
  }

  @Action(InitializeTransportGoodsState)
  initialize(
    ctx: StateContext<TransportGoodsStateModel>,
    { organizationalUnitId }: InitializeTransportGoodsState,
  ): Observable<TypeOfGood[] | null> {
    ctx.patchState({ isLoading: true });

    return this.orgUnitService
      .getOrganizationalUnit(organizationalUnitId, [
        'organizationalUnitTransportGoods',
      ])
      .pipe(
        map((ou) => ou.organizationalUnitTransportGoods ?? []),
        tap((transportGoods: TypeOfGood[]) => {
          ctx.patchState({ transportGoods });
          this.storage.set(STATE_KEY, transportGoods);
        }),
        catchError(() => of(null)),
        finalize(() => ctx.patchState({ isLoading: false })),
      );
  }
}
